import { useEffect, useRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";

export type VideoJSProps = {
	src?: string;

	onReady?: (player: any) => void;

	openInModal?: boolean;

	style?: React.CSSProperties;
};

export const VideoJS = ({ src, onReady, style }: VideoJSProps) => {
	const videoRef = useRef<any>(null);
	const playerRef = useRef<any>(null);

	useEffect(() => {
		const options: any = {
			autoplay: false,
			controls: true,
			responsive: false,
			fluid: false,
			sources: [],
			poster: ""
		};

		if (src) {
			// `withCredentials: true` makes the underlying videojs-http-streaming
			// loader send cookies on the manifest and every segment fetch. The
			// playback URL points at the media subdomain whose cookie was set
			// by the bearer endpoint, so without this the subsequent segment
			// requests would arrive unauthenticated and 401 from the proxy.
			options.sources = [
				{ src, type: "application/x-mpegURL", withCredentials: true }
			];
		}

		// Make sure Video.js player is only initialized once
		if (!playerRef.current) {
			// The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
			const videoElement = document.createElement("video-js");

			videoElement.classList.add("vjs-big-play-centered");
			videoRef.current.appendChild(videoElement);

			const player = (playerRef.current = videojs(videoElement, options, () => {
				onReady && onReady(player);
			}));

			// You could update an existing player in the `else` block here
			// on prop change, for example:
		} else {
			const player = playerRef.current;

			player.autoplay(options.autoplay);
			player.src(options.sources);
		}
	}, [videoRef, src]);

	// Dispose the Video.js player when the functional component unmounts
	useEffect(() => {
		const player = playerRef.current;

		return () => {
			if (player && !player.isDisposed()) {
				player.dispose();
				playerRef.current = null;
			}
		};
	}, [playerRef]);

	return (
		<div data-vjs-player style={style}>
			<div ref={videoRef} style={style} />
		</div>
	);
};

export default VideoJS;
